import React from 'react';
import { Box, Typography, Card, CardContent, Stack, Grid2, Divider } from '@mui/material';
import { SalesReturn } from './SalesReturnHelper';
import { ReportHeaderSx } from '../../Inventory/Common/inventory.helper';
import { itemHeaderSx } from '../../Inventory/MaterialConsumption/MaterialConsumption.helper';
import { ClinicHeader, ReportFooterTimestamp } from '../../../utils/reportUtils';
import { useTranslation } from 'react-i18next';

interface SalesReturnReportPreviewProps {
  reportData: SalesReturn | null;
}

// Helper to safely format currency values
const formatCurrency = (value: string | number | undefined | null): string => {
  const num = Number(value);
  return isNaN(num) ? '0.00' : num.toFixed(2);
};

// InfoRow component matching GRN pattern
const InfoRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Grid2 container size={{ xs: 12 }} sx={itemHeaderSx.infoRowContainer}>
    <Grid2 sx={itemHeaderSx.infoRowLabel}>
      <Typography variant="subtitle2" sx={itemHeaderSx.infoRowLabel}>{label}</Typography>
    </Grid2>
    <Grid2 sx={itemHeaderSx.infoRowValue}>
      <Typography variant="body2" component="div" sx={itemHeaderSx.infoRowValue}>{value ?? '-'}</Typography>
    </Grid2>
  </Grid2>
);

const SalesReturnReportPreview: React.FC<SalesReturnReportPreviewProps> = ({ reportData }) => {
  const { t } = useTranslation('sales_return');
  
  if (!reportData) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography>{t('Report_No_Data')}</Typography>
      </Box>
    );
  }

  const items = reportData.return_items ?? [];

  return (
    <Box sx={{ p: 5, backgroundColor: '#fff', color: '#333', fontFamily: 'Arial, sans-serif' }}>
      <ClinicHeader />

      {/* Sales Return Details Header */}
      <Card elevation={2} sx={itemHeaderSx.card}>
        <CardContent sx={itemHeaderSx.cardContent}>
          <Grid2 container columns={12} spacing={0}>
            <Grid2 size={12} sx={itemHeaderSx.gridContainer}>
              <Typography variant="subtitle1" sx={itemHeaderSx.title}>{t('Report_Sales_Return_Details')}</Typography>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6, md: 6 }} sx={itemHeaderSx.gridLeft}>
              <Stack spacing={1}>
                <InfoRow label={t('Report_Return_No')} value={reportData.return_number} />
                <InfoRow label={t('Report_Patient_Name')} value={reportData.patient_name} />
                <InfoRow label={t('Report_MR_No')} value={reportData.patient_mr_no} />
                <InfoRow label={t('Report_Status')} value={reportData.status} />
              </Stack>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6, md: 6 }} sx={itemHeaderSx.gridRight}>
              <Stack spacing={1}>
                <InfoRow label={t('Report_Return_Date')} value={reportData.return_date} />
                <InfoRow label={t('Report_Age')} value={reportData.patient_age} />
                <InfoRow label={t('Report_Gender')} value={reportData.patient_gender} />
                <InfoRow label={t('Report_Remarks')} value={reportData.remarks || '-'} />
              </Stack>
            </Grid2>
          </Grid2>
        </CardContent>
      </Card>

      <Typography sx={{ ...ReportHeaderSx, mb: 2 }}>{t('Report_Return_Items')}</Typography>

      {/* Items */}
      {items.length > 0 ? (
        <Grid2 container spacing={1} sx={{ width: '100%' }}>
          {items.map((item, index) => (
            <Grid2 size={12} key={index}>
              <Card elevation={2} sx={{ ...itemHeaderSx.card, my: 1 }}>
                <CardContent sx={itemHeaderSx.cardContent}>
                  <Grid2 container columns={12} spacing={0}>
                    {/* Card Header */}
                    <Grid2 size={12} sx={itemHeaderSx.gridContainer}>
                      <Typography variant="subtitle1" sx={itemHeaderSx.title}>
                        {t('Report_Item_Number')}{index + 1}
                      </Typography>
                    </Grid2>

                    {/* Item Details */}
                    <Grid2 size={{ xs: 12, sm: 6, md: 6 }} sx={itemHeaderSx.gridLeft}>
                      <Stack spacing={1}>
                        {/* <InfoRow label="Item Code :" value={item.item_code || '-'} /> */}
                        <InfoRow label={t('Report_Item_Name')} value={item.item_name || '-'} />
                        <InfoRow label={t('Report_Batch_Code')} value={item.batch_code ?? '-'} />
                        <InfoRow label={t('Report_MRP')} value={`₹${formatCurrency(item.mrp)}`} />
                      </Stack>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6, md: 6 }} sx={itemHeaderSx.gridRight}>
                      <Stack spacing={1}>
                        <InfoRow label={t('Report_Item_Code')} value={item.item_code || '-'} />
                        <InfoRow label={t('Report_Quantity')} value={`${item.return_quantity ?? '-'} ${item.return_uom_code || ''}`} />
                        <InfoRow label={t('Report_Net_Amount')} value={`₹${formatCurrency(item.net_amount)}`} />
                      </Stack>
                    </Grid2>

                    {/* Concession Section */}
                    <Grid2 size={12} sx={itemHeaderSx.gridContainer}>
                      <Divider sx={{ my: 1.5 }} />
                      <Typography variant="subtitle1" sx={itemHeaderSx.title}>{t('Report_Concession')}</Typography>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6, md: 6 }} sx={itemHeaderSx.gridLeft}>
                      <Stack spacing={1}>
                        <InfoRow label={t('Report_Concession_Percentage')} value={`${formatCurrency(item.concession_percentage)}%`} />
                      </Stack>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6, md: 6 }} sx={itemHeaderSx.gridRight}>
                      <Stack spacing={1}>
                        <InfoRow label={t('Report_Concession_Amount')} value={`₹${formatCurrency(item.concession_amount)}`} />
                      </Stack>
                    </Grid2>

                    {/* Tax Section */}
                    <Grid2 size={12} sx={itemHeaderSx.gridContainer}>
                      <Divider sx={{ my: 1.5 }} />
                      <Typography variant="subtitle1" sx={itemHeaderSx.title}>{t('Report_Tax_Details')}</Typography>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6, md: 6 }} sx={itemHeaderSx.gridLeft}>
                      <Stack spacing={1}>
                        <InfoRow label={t('Report_SGST_Percentage')} value={`${formatCurrency(item.sgst_percentage)}%`} />
                        <InfoRow label={t('Report_SGST_Amount')} value={`₹${formatCurrency(item.sgst_amount)}`} />
                      </Stack>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6, md: 6 }} sx={itemHeaderSx.gridRight}>
                      <Stack spacing={1}>
                        <InfoRow label={t('Report_CGST_Percentage')} value={`${formatCurrency(item.cgst_percentage)}%`} />
                        <InfoRow label={t('Report_CGST_Amount')} value={`₹${formatCurrency(item.cgst_amount)}`} />
                      </Stack>
                    </Grid2>
                  </Grid2>
                </CardContent>
              </Card>
            </Grid2>
          ))}
        </Grid2>
      ) : (
        <Typography variant="body1">{t('Report_No_Items')}</Typography>
      )}

      {/* Payment Summary */}
      <Card elevation={2} sx={{ ...itemHeaderSx.card, mt: 3 }}>
        <CardContent sx={itemHeaderSx.cardContent}>
          <Grid2 container columns={12} spacing={0}>
            <Grid2 size={12} sx={itemHeaderSx.gridContainer}>
              <Typography variant="subtitle1" sx={itemHeaderSx.title}>{t('Report_Payment_Summary')}</Typography>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6, md: 6 }} sx={itemHeaderSx.gridLeft}>
              <Stack spacing={1}>
                <InfoRow label={t('Report_Net_Amount')} value={`₹${formatCurrency(reportData.net_amount)}`} />
                <InfoRow label={t('Report_Paid_Amount')} value={`₹${formatCurrency(reportData.paid_amount)}`} />
              </Stack>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6, md: 6 }} sx={itemHeaderSx.gridRight}>
              <Stack spacing={1}>
                <InfoRow label={t('Report_Balance_Amount')} value={`₹${formatCurrency(reportData.balance_amount)}`} />
              </Stack>
            </Grid2>
          </Grid2>
        </CardContent>
      </Card>

      <ReportFooterTimestamp />
    </Box>
  );
};

export default SalesReturnReportPreview;
